import React, { useState, useEffect } from "react";
import { Shield, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

/**
 * Modal shadcn para confirmar "Aprobar en silencio" (silent-admin-approve).
 *
 * Reemplaza el `window.prompt()` nativo por una UI consistente con el resto
 * de modales de la app. Solo se muestra a dredondo@valorizasm.com.
 *
 * Props:
 *   open, offerTitle, defaultReason, onCancel, onConfirm(reason), loading
 */
export function SilentAdminModal({
  open,
  offerTitle,
  defaultReason = "modificación cifras aprobadas",
  onCancel,
  onConfirm,
  loading,
}) {
  const [reason, setReason] = useState(defaultReason);

  // Reset reason al abrir
  useEffect(() => {
    if (open) setReason(defaultReason);
  }, [open, defaultReason]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !loading) onCancel(); }}>
      <DialogContent
        className="max-w-lg"
        data-testid="silent-admin-modal"
        onEscapeKeyDown={(e) => { if (loading) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white">
              <Shield size={15} />
            </div>
            🤫 Aprobar en silencio (admin)
          </DialogTitle>
          <DialogDescription className="text-xs pt-1">
            Marca la oferta <strong className="text-gray-800">«{offerTitle || "(sin título)"}»</strong> como{" "}
            <strong className="text-emerald-700">VALIDADA por TIC</strong> +{" "}
            <strong className="text-blue-700">APROBADA por CFO</strong> sin enviar emails a los aprobadores.
            <br />
            Ideal para modificaciones sobre ofertas ya aprobadas — quedará todo trazado en el
            histórico como <code className="text-[10px] bg-gray-100 px-1 rounded">silent_admin_update</code>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <label htmlFor="silent-admin-reason" className="text-xs font-semibold text-gray-600">
            Motivo de la modificación <span className="text-gray-400 font-normal">(obligatorio)</span>
          </label>
          <Textarea
            id="silent-admin-reason"
            data-testid="silent-admin-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ej: cifras aprobadas licitación cambian tras revisión CFO..."
            rows={3}
            disabled={loading}
            className="resize-none text-sm"
            autoFocus
          />
          <p className="text-[10px] text-gray-400">
            💡 Este motivo quedará registrado en el <strong>Historial de Aprobaciones</strong> junto a
            las dos entradas <em>silent_admin_update</em> (TIC + CFO).
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            data-testid="silent-admin-cancel-btn"
          >
            Cancelar
          </Button>
          <Button
            onClick={() => onConfirm(reason.trim() || "modificación")}
            disabled={loading || !reason.trim()}
            data-testid="silent-admin-confirm-btn"
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
          >
            {loading ? (
              <><Loader2 size={13} className="animate-spin mr-1.5" /> Aprobando…</>
            ) : (
              <><Shield size={13} className="mr-1.5" /> Confirmar aprobación silenciosa</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
